import type { FontFamily } from './types.js';

/**
 * Board palette.
 *
 * Colours are stored on nodes as literal hex strings rather than token names.
 * A board outlives any given theme, and a shape a user coloured yellow in 2026
 * must still be yellow after a redesign, so the document keeps the value.
 */

export const STICKY_COLORS = [
  { name: 'Жёлтый', value: '#FFE066', text: '#1F1A00' },
  { name: 'Оранжевый', value: '#FFB84D', text: '#2A1600' },
  { name: 'Розовый', value: '#FF9EC4', text: '#2E0B1B' },
  { name: 'Фиолетовый', value: '#C39BFF', text: '#1E0B33' },
  { name: 'Синий', value: '#8FC5FF', text: '#031A33' },
  { name: 'Голубой', value: '#7FE3E0', text: '#00231F' },
  { name: 'Зелёный', value: '#A6E28F', text: '#0C2200' },
  { name: 'Лаймовый', value: '#DCE775', text: '#1C2000' },
  { name: 'Серый', value: '#D5DAE1', text: '#12161C' },
  { name: 'Белый', value: '#FFFFFF', text: '#12161C' },
] as const;

export const SHAPE_FILLS = [
  { name: 'Нет', value: 'transparent' },
  { name: 'Белый', value: '#FFFFFF' },
  { name: 'Сланцевый', value: '#E2E8F0' },
  { name: 'Жёлтый', value: '#FFF0B3' },
  { name: 'Оранжевый', value: '#FFD9A8' },
  { name: 'Красный', value: '#FFC9C9' },
  { name: 'Розовый', value: '#FFCCE0' },
  { name: 'Фиолетовый', value: '#E0D0FF' },
  { name: 'Синий', value: '#CBE2FF' },
  { name: 'Голубой', value: '#C0EFEE' },
  { name: 'Зелёный', value: '#CBEFBB' },
  { name: 'Тёмный', value: '#2B3340' },
] as const;

export const STROKE_COLORS = [
  { name: 'Чернильный', value: '#1A1F28' },
  { name: 'Сланцевый', value: '#5A6472' },
  { name: 'Красный', value: '#E03131' },
  { name: 'Оранжевый', value: '#F76707' },
  { name: 'Янтарный', value: '#F59F00' },
  { name: 'Зелёный', value: '#2F9E44' },
  { name: 'Бирюзовый', value: '#0CA678' },
  { name: 'Синий', value: '#1C7ED6' },
  { name: 'Индиго', value: '#4263EB' },
  { name: 'Фиолетовый', value: '#7048E8' },
  { name: 'Розовый', value: '#D6336C' },
  { name: 'Белый', value: '#FFFFFF' },
] as const;

/** Cursor colours are assigned round-robin from a user id hash. */
export const PRESENCE_COLORS = [
  '#F0435F',
  '#F97316',
  '#F59E0B',
  '#22C55E',
  '#10B981',
  '#06B6D4',
  '#3B82F6',
  '#6366F1',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
  '#EAB308',
] as const;

export function presenceColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % PRESENCE_COLORS.length;
  return PRESENCE_COLORS[idx]!;
}

/** Text colour that stays readable on an arbitrary fill. */
export function contrastText(hex: string, dark = '#12161C', light = '#FFFFFF'): string {
  const c = hex.replace('#', '');
  if (c.length !== 6 && c.length !== 3) return dark;
  const full =
    c.length === 3
      ? c
          .split('')
          .map((ch) => ch + ch)
          .join('')
      : c;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const channel = (v: number) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return luminance > 0.45 ? dark : light;
}

export const FONT_STACKS: Record<FontFamily, string> = {
  sans: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
  serif: '"Source Serif 4", Georgia, "Times New Roman", serif',
  mono: '"JetBrains Mono", "SF Mono", Consolas, monospace',
  marker: '"Permanent Marker", "Comic Sans MS", "Segoe Print", cursive',
};

export const FONT_SIZES = [10, 12, 14, 16, 18, 24, 32, 40, 48, 64, 96, 144] as const;
